import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import ExcelJS from 'exceljs';
import { productService } from '../../services/productService';
import { classificationService } from '../../services/classificationService';
import ConfirmModal from '../../components/Common/ConfirmModal';
import Toast from '../../components/Common/Toast';
import ProductsExcelToolbar from '../../components/Products/ProductsExcelToolbar';
import ProductsBulkEditModal from '../../components/Products/ProductsBulkEditModal';
import { useModuleSettings } from '../../contexts/ModuleSettingsContext';
import MutateGate from '../../components/Common/MutateGate';
import { useCanMutate } from '../../hooks/useCanMutate';
import { authService } from '../../services/authService';
import { getModuleSettings } from '../../services/moduleSettingsService';

const Products = () => {
  const canMutate = useCanMutate();
  const {
    produccionEnabled,
    produccionConcesionariaEnabled,
    restaurantesEnabled,
    ventasEnabled,
    machineryEnabled,
    loaded: moduleSettingsLoaded,
  } = useModuleSettings();
  const isSuperAdmin = Boolean(authService.getCurrentUser()?.is_super_admin);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [warehouseVisibilityEnabled, setWarehouseVisibilityEnabled] = useState(false);
  /** Producto terminado vinculado a receta elaborada (módulo Producción o legado concesionaria/restaurantes). */
  const showRecetaColumn =
    moduleSettingsLoaded &&
    (produccionEnabled || produccionConcesionariaEnabled || restaurantesEnabled);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubcategory, setSelectedSubcategory] = useState('');
  const [selectedFamily, setSelectedFamily] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [selectedProductIds, setSelectedProductIds] = useState(() => new Set());
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const hasLoaded = useRef(false);
  const hasLoadedProducts = useRef(false);

  useEffect(() => {
    if (!hasLoaded.current) {
      hasLoaded.current = true;
      loadClassificationData();
    }
  }, []);

  useEffect(() => {
    if (!isSuperAdmin) return;
    getModuleSettings()
      .then((res) => {
        if (res?.success && res.data) {
          setWarehouseVisibilityEnabled(Boolean(res.data.product_warehouse_visibility_enabled));
        }
      })
      .catch(() => {});
  }, [isSuperAdmin]);

  useEffect(() => {
    if (!hasLoadedProducts.current) {
      hasLoadedProducts.current = true;
      loadProducts();
    }
  }, []);

  useEffect(() => {
    if (selectedCategory) {
      loadSubcategories();
    } else {
      setSubcategories([]);
      setSelectedSubcategory('');
    }
  }, [selectedCategory]);

  useEffect(() => {
    if (selectedSubcategory) {
      loadFamilies();
    } else {
      setFamilies([]);
      setSelectedFamily('');
    }
  }, [selectedSubcategory]);

  const loadClassificationData = async () => {
    try {
      const [categoriesRes, subcategoriesRes, familiesRes] = await Promise.all([
        classificationService.getCategories({ active_only: false }),
        classificationService.getSubcategories({ active_only: false }),
        classificationService.getFamilies({ active_only: false })
      ]);

      if (categoriesRes.success) setCategories(categoriesRes.data);
      if (subcategoriesRes.success) setSubcategories(subcategoriesRes.data);
      if (familiesRes.success) setFamilies(familiesRes.data);
    } catch (error) {
      console.error('Error al cargar datos de clasificación:', error);
    }
  };

  const loadSubcategories = async () => {
    try {
      const response = await classificationService.getSubcategories({
        category_id: selectedCategory,
        active_only: false
      });
      if (response.success) {
        setSubcategories(response.data);
      }
    } catch (error) {
      console.error('Error al cargar subcategorías:', error);
    }
  };

  const loadFamilies = async () => {
    try {
      const response = await classificationService.getFamilies({
        subcategory_id: selectedSubcategory,
        active_only: false
      });
      if (response.success) {
        setFamilies(response.data);
      }
    } catch (error) {
      console.error('Error al cargar familias:', error);
    }
  };

  const loadProducts = async () => {
    setLoadingProducts(true);
    console.log('📡 [Products] Cargando productos desde backend...');
    
    try {
      const response = await productService.getProducts();
      
      if (response.success && response.data) {
        console.log('✅ [Products] Productos cargados exitosamente:', response.data.length);
        setProducts(response.data);
      } else {
        console.warn('⚠️ [Products] Respuesta sin éxito:', response);
        setToast({
          isOpen: true,
          message: 'Error al cargar los productos',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('❌ [Products] Error al cargar productos:', error);
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al cargar los productos',
        type: 'error'
      });
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleDeleteClick = (product) => {
    if (product.linked_to_recipe) {
      setToast({
        isOpen: true,
        message:
          'Este producto está vinculado a una receta elaborada (producto terminado). No puede eliminarse desde productos; gestione la receta en clasificación de recetas.',
        type: 'error',
      });
      return;
    }
    setProductToDelete(product);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!productToDelete) return;
    
    setLoading(true);
    console.log('🗑️ [Products] Eliminando producto:', productToDelete.id);
    
    try {
      const response = await productService.deleteProduct(productToDelete.id);
      
      if (response.success) {
        console.log('✅ [Products] Producto eliminado exitosamente');
        // Recargar productos
        await loadProducts();
        setSelectedProductIds((prev) => {
          const next = new Set(prev);
          next.delete(productToDelete.id);
          return next;
        });
        setIsDeleteModalOpen(false);
        setProductToDelete(null);
        setToast({
          isOpen: true,
          message: 'Producto eliminado exitosamente',
          type: 'success'
        });
      } else {
        throw new Error(response.message || 'Error al eliminar el producto');
      }
    } catch (error) {
      console.error('❌ [Products] Error al eliminar producto:', error);
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al eliminar el producto',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Filtrar productos
  const filteredProducts = products.filter(product => {
    // Filtro por búsqueda
    const matchesSearch = !searchTerm || 
      (product.sku && product.sku.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (product.name && product.name.toLowerCase().includes(searchTerm.toLowerCase()));

    // Filtro por categoría
    const matchesCategory = !selectedCategory || 
      product.family?.subcategory?.category?.id === parseInt(selectedCategory) ||
      product.family?.subcategory?.category_id === parseInt(selectedCategory);

    // Filtro por subcategoría
    const matchesSubcategory = !selectedSubcategory || 
      product.family?.subcategory?.id === parseInt(selectedSubcategory) ||
      product.family?.subcategory_id === parseInt(selectedSubcategory);

    // Filtro por familia
    const matchesFamily = !selectedFamily || 
      product.family_id === parseInt(selectedFamily) ||
      product.family?.id === parseInt(selectedFamily);

    // Filtro por estado
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && product.is_active) ||
      (statusFilter === 'inactive' && !product.is_active);

    return matchesSearch && matchesCategory && matchesSubcategory && 
           matchesFamily && matchesStatus;
  });

  const recipeVendibleEditable = Boolean(produccionEnabled && ventasEnabled);
  const isBulkSelectable = (product) =>
    canMutate && (!product.linked_to_recipe || recipeVendibleEditable);

  const bulkSelectableProducts = filteredProducts.filter(isBulkSelectable);
  const selectedCount = bulkSelectableProducts.filter((p) => selectedProductIds.has(p.id)).length;
  const allBulkSelected =
    bulkSelectableProducts.length > 0 &&
    bulkSelectableProducts.every((p) => selectedProductIds.has(p.id));

  const toggleSelectProduct = (product) => {
    if (!isBulkSelectable(product)) return;
    setSelectedProductIds((prev) => {
      const next = new Set(prev);
      if (next.has(product.id)) next.delete(product.id);
      else next.add(product.id);
      return next;
    });
  };

  const toggleSelectAllBulk = () => {
    if (allBulkSelected) {
      setSelectedProductIds(new Set());
      return;
    }
    setSelectedProductIds(new Set(bulkSelectableProducts.map((p) => p.id)));
  };

  const handleBulkDeleteConfirm = async () => {
    const ids = bulkSelectableProducts
      .filter((p) => selectedProductIds.has(p.id))
      .map((p) => p.id);
    if (ids.length === 0) return;

    setBulkDeleting(true);
    try {
      const response = await productService.bulkDeleteProducts(ids);
      const deletedCount = response.deleted_count ?? response.deleted?.length ?? 0;
      const errorCount = response.error_count ?? response.errors?.length ?? 0;
      const errorSamples = (response.errors || [])
        .slice(0, 3)
        .map((e) => e.sku || e.id)
        .filter(Boolean);

      await loadProducts();
      setSelectedProductIds(new Set());
      setIsBulkDeleteModalOpen(false);

      const type = deletedCount === 0 ? 'error' : errorCount > 0 ? 'warning' : 'success';
      const parts = [response.message || `Eliminados: ${deletedCount}.`];
      if (errorSamples.length > 0) {
        parts.push(`Omitidos (ej.): ${errorSamples.join(', ')}`);
      }
      setToast({ isOpen: true, message: parts.join(' '), type });
    } catch (error) {
      setToast({
        isOpen: true,
        message: error.response?.data?.message || error.message || 'Error en el borrado masivo',
        type: 'error',
      });
    } finally {
      setBulkDeleting(false);
    }
  };

  // Calcular estadísticas (basadas en todos los productos, no solo los filtrados)
  const totalProducts = products.length;
  const activeProducts = products.filter(p => p.is_active).length;
  const inactiveProducts = products.filter(p => !p.is_active).length;

  // Exportar productos a Excel
  const exportToExcel = async () => {
    try {
      const includeRecetaCol = showRecetaColumn;
      const lastCol = includeRecetaCol ? 'I' : 'H';

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Productos');

      // Estilos
      const headerStyle = {
        font: { bold: true, color: { argb: 'FFFFFFFF' } },
        fill: {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF1F2937' }
        },
        alignment: { horizontal: 'center', vertical: 'middle', wrapText: true },
        border: {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        }
      };

      const titleStyle = {
        font: { bold: true, size: 16 },
        alignment: { horizontal: 'center', vertical: 'middle' }
      };

      // Título
      worksheet.mergeCells(`A1:${lastCol}1`);
      worksheet.getCell('A1').value = 'Lista de Productos';
      worksheet.getCell('A1').style = titleStyle;
      worksheet.getRow(1).height = 25;

      // Fecha de exportación
      worksheet.mergeCells(`A2:${lastCol}2`);
      worksheet.getCell('A2').value = `Fecha de exportación: ${new Date().toLocaleString('es-PE', { dateStyle: 'long', timeStyle: 'short' })}`;
      worksheet.getCell('A2').style = { alignment: { horizontal: 'center' }, font: { italic: true } };
      worksheet.getRow(2).height = 20;

      // Información de filtros
      if (searchTerm || selectedCategory || selectedSubcategory || selectedFamily || statusFilter !== 'all') {
        const filterInfo = [];
        if (searchTerm) filterInfo.push(`Búsqueda: ${searchTerm}`);
        if (selectedCategory) {
          const category = categories.find(c => c.id === parseInt(selectedCategory));
          if (category) filterInfo.push(`Categoría: ${category.name}`);
        }
        if (selectedSubcategory) {
          const subcategory = subcategories.find(s => s.id === parseInt(selectedSubcategory));
          if (subcategory) filterInfo.push(`Subcategoría: ${subcategory.name}`);
        }
        if (selectedFamily) {
          const family = families.find(f => f.id === parseInt(selectedFamily));
          if (family) filterInfo.push(`Familia: ${family.name}`);
        }
        if (statusFilter !== 'all') {
          filterInfo.push(`Estado: ${statusFilter === 'active' ? 'Activos' : 'Inactivos'}`);
        }
        
        worksheet.mergeCells(`A3:${lastCol}3`);
        worksheet.getCell('A3').value = `Filtros aplicados: ${filterInfo.join(' | ')}`;
        worksheet.getCell('A3').style = { alignment: { horizontal: 'center' }, font: { italic: true, size: 10 } };
        worksheet.getRow(3).height = 18;
      }

      // Encabezados
      const headers = [
        'SKU',
        'Nombre',
        'Categoría',
        'Subcategoría',
        'Familia',
        'Unidad',
        'Stock Mínimo',
        'Estado',
        ...(includeRecetaCol ? ['Receta (producto terminado)'] : []),
      ];

      const headerRow = worksheet.addRow(headers);
      headerRow.height = 25;
      headerRow.eachCell((cell, colNumber) => {
        cell.style = headerStyle;
      });

      // Datos
      filteredProducts.forEach((product) => {
        const categoryName = product.family?.subcategory?.category?.name || 'Sin categoría';
        const subcategoryName = product.family?.subcategory?.name || 'Sin subcategoría';
        const familyName = product.family?.name || 'Sin familia';

        const row = worksheet.addRow([
          product.sku || '-',
          product.name || '-',
          categoryName,
          subcategoryName,
          familyName,
          product.unit_of_measure || '-',
          product.min_stock || 0,
          product.is_active ? 'Activo' : 'Inactivo',
          ...(includeRecetaCol
            ? [product.linked_to_recipe ? 'Sí' : 'No']
            : []),
        ]);

        row.height = 20;

        // Aplicar bordes a todas las celdas
        row.eachCell((cell) => {
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };
          cell.alignment = { vertical: 'middle', wrapText: true };
        });

        // Alineación específica
        row.getCell(1).alignment = { horizontal: 'left', vertical: 'middle' }; // SKU
        row.getCell(2).alignment = { horizontal: 'left', vertical: 'middle' }; // Nombre
        row.getCell(3).alignment = { horizontal: 'left', vertical: 'middle' }; // Categoría
        row.getCell(4).alignment = { horizontal: 'left', vertical: 'middle' }; // Subcategoría
        row.getCell(5).alignment = { horizontal: 'left', vertical: 'middle' }; // Familia
        row.getCell(6).alignment = { horizontal: 'center', vertical: 'middle' }; // Unidad
        row.getCell(7).alignment = { horizontal: 'center', vertical: 'middle' }; // Stock Mínimo
        row.getCell(8).alignment = { horizontal: 'center', vertical: 'middle' }; // Estado
        if (includeRecetaCol) {
          row.getCell(9).alignment = { horizontal: 'center', vertical: 'middle' };
        }
      });

      // Ajustar ancho de columnas
      worksheet.columns = [
        { width: 25 }, // SKU
        { width: 35 }, // Nombre
        { width: 25 }, // Categoría
        { width: 25 }, // Subcategoría
        { width: 25 }, // Familia
        { width: 12 }, // Unidad
        { width: 15 }, // Stock Mínimo
        { width: 12 }, // Estado
        ...(includeRecetaCol ? [{ width: 22 }] : []), // Receta
      ];

      // Generar archivo
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Productos_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);

      setToast({
        isOpen: true,
        message: `Se exportaron ${filteredProducts.length} producto(s) a Excel`,
        type: 'success'
      });
    } catch (error) {
      console.error('Error al exportar a Excel:', error);
      setToast({
        isOpen: true,
        message: 'Error al exportar los productos a Excel',
        type: 'error'
      });
    }
  };

  return (
    <div>
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-800">Productos</h1>
                <p className="text-gray-600 mt-1">Gestión de inventario de productos</p>
              </div>
              <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
                <MutateGate>
                  <ProductsExcelToolbar
                    onImported={loadProducts}
                    setToast={setToast}
                    insumoEnabled={
                      produccionEnabled || produccionConcesionariaEnabled || restaurantesEnabled
                    }
                    machineryEnabled={machineryEnabled}
                  />
                </MutateGate>
                {isSuperAdmin && (
                  <button
                    type="button"
                    onClick={() => setBulkEditOpen(true)}
                    disabled={loadingProducts || products.length === 0}
                    className="px-4 py-2 border border-violet-300 text-violet-800 bg-violet-50 hover:bg-violet-100 rounded-md font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    Edición masiva
                  </button>
                )}
                <button
                  type="button"
                  onClick={exportToExcel}
                  disabled={loadingProducts || filteredProducts.length === 0}
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Exportar a Excel
                </button>
                <MutateGate>
                  <Link
                    to="/products/create"
                    className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md font-medium transition-colors inline-block text-center"
                  >
                    + Nuevo Producto
                  </Link>
                </MutateGate>
              </div>
            </div>

            {/* Breadcrumb */}
            <nav className="text-sm text-gray-600 mb-4">
              <Link to="/dashboard" className="hover:text-teal-600">Dashboard</Link>
              <span className="mx-2">/</span>
              <span className="text-gray-800">Productos</span>
            </nav>

            {/* Filtros: una sola línea; scroll horizontal si no cabe en pantallas estrechas */}
            <div className="bg-white rounded-lg shadow-md p-3 mb-4">
              <div className="flex flex-nowrap items-end gap-2 overflow-x-auto pb-1 -mx-0.5 px-0.5">
                <div className="flex flex-col gap-0.5 flex-shrink-0 w-40 sm:w-44">
                  <label className="text-xs font-medium text-gray-600 whitespace-nowrap">Categoría</label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => {
                      setSelectedCategory(e.target.value);
                      setSelectedSubcategory('');
                      setSelectedFamily('');
                    }}
                    className="w-full px-2 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="">Todas</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.code} — {cat.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-0.5 flex-shrink-0 w-40 sm:w-48">
                  <label className="text-xs font-medium text-gray-600 whitespace-nowrap">Subcategoría</label>
                  <select
                    value={selectedSubcategory}
                    onChange={(e) => {
                      setSelectedSubcategory(e.target.value);
                      setSelectedFamily('');
                    }}
                    className="w-full px-2 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
                    disabled={!selectedCategory}
                  >
                    <option value="">Todas</option>
                    {subcategories.map((sub) => (
                      <option key={sub.id} value={sub.id}>
                        {sub.code} — {sub.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-0.5 flex-shrink-0 w-40 sm:w-48">
                  <label className="text-xs font-medium text-gray-600 whitespace-nowrap">Familia</label>
                  <select
                    value={selectedFamily}
                    onChange={(e) => setSelectedFamily(e.target.value)}
                    className="w-full px-2 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
                    disabled={!selectedSubcategory}
                  >
                    <option value="">Todas</option>
                    {families.map((fam) => (
                      <option key={fam.id} value={fam.id}>
                        {fam.code} — {fam.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-0.5 flex-shrink-0 w-36 sm:w-40">
                  <label className="text-xs font-medium text-gray-600 whitespace-nowrap">Estado</label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full px-2 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="all">Todos</option>
                    <option value="active">Activos</option>
                    <option value="inactive">Inactivos</option>
                  </select>
                </div>
                <div className="flex flex-col gap-0.5 flex-shrink-0 min-w-[14rem] w-[min(100%,20rem)] sm:min-w-[16rem] sm:w-72">
                  <label className="text-xs font-medium text-gray-600 whitespace-nowrap">Buscar</label>
                  <input
                    type="text"
                    placeholder="SKU, nombre o código de barras…"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedCategory('');
                    setSelectedSubcategory('');
                    setSelectedFamily('');
                    setSearchTerm('');
                    setStatusFilter('all');
                  }}
                  className="flex-shrink-0 px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors self-end"
                >
                  Limpiar
                </button>
              </div>
            </div>
          </div>

          {/* Estadísticas - ENCIMA DE LA TABLA */}
          <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg shadow-md p-4">
              <div className="text-sm text-gray-600">Total Productos</div>
              <div className="text-2xl font-bold text-gray-800">{totalProducts}</div>
            </div>
            <div className="bg-white rounded-lg shadow-md p-4">
              <div className="text-sm text-gray-600">Productos Activos</div>
              <div className="text-2xl font-bold text-green-600">{activeProducts}</div>
            </div>
            <div className="bg-white rounded-lg shadow-md p-4">
              <div className="text-sm text-gray-600">Productos Inactivos</div>
              <div className="text-2xl font-bold text-red-600">{inactiveProducts}</div>
            </div>
          </div>

          <MutateGate>
            {bulkSelectableProducts.length > 0 ? (
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <button
                  type="button"
                  onClick={toggleSelectAllBulk}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  {allBulkSelected ? 'Quitar selección' : 'Seleccionar visibles'}
                </button>
                {selectedCount > 0 ? (
                  <button
                    type="button"
                    onClick={() => setIsBulkDeleteModalOpen(true)}
                    className="px-3 py-2 border border-red-300 rounded-md text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100"
                  >
                    Borrar seleccionados ({selectedCount})
                  </button>
                ) : null}
                <span className="text-xs text-gray-500">
                  Solo productos sin movimientos. Las recetas no se incluyen.
                </span>
              </div>
            ) : null}
          </MutateGate>

          {/* Tabla: scroll horizontal, texto con wrap, acciones fijas a la derecha */}
          <div className="bg-white rounded-lg shadow-md w-full max-w-full overflow-x-auto">
            {loadingProducts ? (
              <div className="p-8 text-center">
                <div className="inline-block">
                  <svg className="animate-spin h-8 w-8 text-teal-600" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
                <p className="mt-4 text-gray-600">Cargando productos...</p>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                {searchTerm || selectedCategory
                  ? 'No se encontraron productos con los filtros aplicados'
                  : 'No hay productos registrados'}
              </div>
            ) : (
              <table className="w-full min-w-[min(100%,56rem)] divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {canMutate ? (
                      <th className="px-3 py-3 text-left w-10">
                        <input
                          type="checkbox"
                          checked={allBulkSelected}
                          onChange={toggleSelectAllBulk}
                          disabled={bulkSelectableProducts.length === 0}
                          aria-label="Seleccionar todos los productos visibles eliminables"
                          className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                        />
                      </th>
                    ) : null}
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap max-w-[9rem]">
                      SKU
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-w-[12rem]">
                      Nombre
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-w-[18rem]">
                      Clasificación
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Unidad
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Stock mín.
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Estado
                    </th>
                    {showRecetaColumn ? (
                      <th
                        className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap max-w-[6rem]"
                        title="Producto terminado generado desde una receta elaborada"
                      >
                        Receta
                      </th>
                    ) : null}
                    <th className="sticky right-0 z-20 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 border-l border-gray-200 shadow-[-8px_0_12px_-6px_rgba(0,0,0,0.12)] whitespace-nowrap">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredProducts.map((product) => (
                    <tr key={product.id} className="group hover:bg-gray-50">
                      {canMutate ? (
                        <td className="px-3 py-3 align-top w-10">
                          {isBulkSelectable(product) ? (
                            <input
                              type="checkbox"
                              checked={selectedProductIds.has(product.id)}
                              onChange={() => toggleSelectProduct(product)}
                              aria-label={`Seleccionar ${product.name}`}
                              className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                            />
                          ) : null}
                        </td>
                      ) : null}
                      <td className="px-3 py-3 align-top max-w-[9rem] break-all">
                        <Link
                          to={`/products/${product.id}`}
                          className="text-sm font-mono font-medium text-teal-600 hover:text-teal-900"
                        >
                          {product.sku}
                        </Link>
                      </td>
                      <td className="px-3 py-3 align-top max-w-[12rem] break-words">
                        <span className="text-sm font-medium text-gray-900">{product.name}</span>
                      </td>
                      <td className="px-3 py-3 align-top max-w-[18rem] break-words">
                        {product.family ? (
                          <span className="text-sm text-gray-900">
                            <span className="font-medium text-gray-800">
                              {product.family.subcategory?.category?.code || 'N/A'} —{' '}
                              {product.family.subcategory?.code || 'N/A'} — {product.family.code || 'N/A'}
                            </span>
                            <span className="text-gray-500">
                              {' '}
                              · {product.family.subcategory?.category?.name || 'N/A'} →{' '}
                              {product.family.subcategory?.name || 'N/A'} → {product.family.name || 'N/A'}
                            </span>
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">Sin clasificación</span>
                        )}
                      </td>
                      <td className="px-2 py-3 align-top whitespace-nowrap">
                        <span className="text-sm text-gray-900">{product.unit_of_measure}</span>
                      </td>
                      <td className="px-2 py-3 align-top whitespace-nowrap">
                        <span className="text-sm text-gray-900">{product.min_stock}</span>
                      </td>
                      <td className="px-2 py-3 align-top whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs rounded-full ${
                            product.is_active
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {product.is_active ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      {showRecetaColumn ? (
                        <td className="px-2 py-3 align-top whitespace-nowrap max-w-[6rem]">
                          {product.linked_to_recipe ? (
                            <span
                              className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-800 ring-1 ring-inset ring-violet-200"
                              title="Producto terminado vinculado a una receta elaborada. Para quitarlo, elimine o cambie la receta en clasificación de recetas."
                            >
                              <svg
                                className="h-3.5 w-3.5 shrink-0 text-violet-600"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                aria-hidden
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.053 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"
                                />
                              </svg>
                              Sí
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                      ) : null}
                      <td className="sticky right-0 z-10 px-3 py-3 align-top text-sm font-medium bg-white border-l border-gray-200 shadow-[-8px_0_12px_-6px_rgba(0,0,0,0.12)] group-hover:bg-gray-50">
                        <div className="flex flex-col gap-1 whitespace-nowrap">
                          {product.linked_to_recipe && !recipeVendibleEditable ? (
                            <span
                              className="text-xs text-gray-400 cursor-default"
                              title="Producto terminado de receta: edítelo desde clasificación de recetas"
                            >
                              —
                            </span>
                          ) : (
                            <Link
                              to={`/products/${product.id}/edit`}
                              className="text-purple-600 hover:text-purple-900"
                              title={
                                product.linked_to_recipe
                                  ? (canMutate ? 'Marcar vendible' : 'Ver detalle')
                                  : (canMutate ? 'Editar' : 'Ver detalle')
                              }
                            >
                              {product.linked_to_recipe
                                ? (canMutate ? 'Vendible' : 'Ver')
                                : (canMutate ? 'Editar' : 'Ver')}
                            </Link>
                          )}
                          <MutateGate>
                            {!product.linked_to_recipe && (
                              <button
                                type="button"
                                onClick={() => handleDeleteClick(product)}
                                className="text-left text-red-600 hover:text-red-900"
                                title="Eliminar"
                              >
                                Eliminar
                              </button>
                            )}
                          </MutateGate>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Modal de Confirmación de Eliminación */}
        <ConfirmModal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setProductToDelete(null);
          }}
          onConfirm={handleDeleteConfirm}
          title="Eliminar Producto"
          message={`¿Estás seguro de que deseas eliminar el producto "${productToDelete?.name}" (${productToDelete?.sku})? Esta acción no se puede deshacer.`}
          confirmText="Eliminar"
          cancelText="Cancelar"
          type="danger"
        />

        <ConfirmModal
          isOpen={isBulkDeleteModalOpen}
          onClose={() => {
            if (bulkDeleting) return;
            setIsBulkDeleteModalOpen(false);
          }}
          onConfirm={handleBulkDeleteConfirm}
          title="Borrado masivo de productos"
          message={`¿Eliminar ${selectedCount} producto(s) seleccionado(s)? Solo se borrarán los que no tengan movimientos ni estén vinculados a recetas. Esta acción no se puede deshacer.`}
          confirmText={bulkDeleting ? 'Eliminando…' : 'Eliminar'}
          cancelText="Cancelar"
          type="danger"
        />

        <ProductsBulkEditModal
          isOpen={bulkEditOpen}
          onClose={() => setBulkEditOpen(false)}
          products={products}
          setToast={setToast}
          onUpdated={loadProducts}
          moduleOptions={{
            insumoEnabled:
              produccionEnabled || produccionConcesionariaEnabled || restaurantesEnabled,
            ventasEnabled,
            produccionEnabled,
            machineryEnabled,
            warehouseVisibilityEnabled,
          }}
        />

        {/* Toast de notificaciones */}
        <Toast
          isOpen={toast.isOpen}
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, isOpen: false })}
        />
      </div>
    </div>
  );
};

export default Products;

